import { useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import styled from '@emotion/styled';
import { Capability } from 'homey-lib/webpack/index.js';

import { useDevice } from '../../../store/devices/useDevices.ts';
import { useCapabilityValue } from '../../../store/devices/useCapabilityValue';

import { getSliderImages } from './getSliderImages';

import { theme } from '../../../theme/theme';

import * as Containers from '../Containers';
import { CapabilitySelect } from '../CapabilitySelect';
import { Slider } from './Slider';
import { IconButton } from '../../buttons/IconButton';

export function SliderAdapter(props) {
  const { device } = useDevice({ deviceId: props.deviceId });

  const [selectedCapabilityId, setSelectedCapabilityId] = useState(() => {
    return props.component.capabilities[0];
  });
  const [value, setValue, capabilityObj] = useCapabilityValue({
    deviceId: props.deviceId,
    capabilityId: selectedCapabilityId,
  });

  const debouncedCall = useMemo(() => {
    return debounce((newValue) => {
      setValue(newValue).catch((error) => {});
    }, 500);
  }, [setValue]);

  if (capabilityObj == null) return null;

  const images = getSliderImages(capabilityObj.id, device?.class);

  function handleChange([newValue]) {
    setValue(newValue, { local: true });
    debouncedCall(newValue);
  }

  return (
    <Containers.Control>
      <Containers.Select>
        {props.component.capabilities.length > 1 && (
          <CapabilitySelect
            selectedCapabilityId={selectedCapabilityId}
            componentCapabilities={props.component.capabilities}
            capabilitiesObj={device?.capabilitiesObj}
            onSelectionChange={setSelectedCapabilityId}
          />
        )}
      </Containers.Select>
      <Containers.Action>
        <SliderAdapter.IconButtonWrapper>
          <IconButton
            size={theme.icon.size_large}
            url={images.upper}
            color={theme.color.white}
            onPress={() => {
              if (Capability.getBaseId(selectedCapabilityId) === 'volume_set') {
                handleChange([(value ?? 0) + (capabilityObj.step ?? 0.01)]);
                return;
              }

              handleChange([capabilityObj.max ?? 1]);
            }}
          />
        </SliderAdapter.IconButtonWrapper>
        <Slider
          style={{
            flex: '1 1 auto', // Containers.Action is a flex container with column direction
          }}
          minValue={capabilityObj.min ?? 0}
          maxValue={capabilityObj.max ?? 1}
          step={capabilityObj.step ?? 0.01}
          aria-label={capabilityObj.title ?? 'Slider'}
          value={[value ?? '']}
          formatOptions={{ style: capabilityObj.units === '%' ? 'percent' : 'decimal' }}
          onChange={handleChange}
        />
        <SliderAdapter.IconButtonWrapper>
          <IconButton
            size={theme.icon.size_large}
            url={images.lower}
            color={theme.color.white}
            onPress={() => {
              if (Capability.getBaseId(selectedCapabilityId) === 'volume_set') {
                handleChange([(value ?? 0) - (capabilityObj.step ?? 0.01)]);
                return;
              }

              handleChange([capabilityObj.min ?? 0]);
            }}
          />
        </SliderAdapter.IconButtonWrapper>
      </Containers.Action>
    </Containers.Control>
  );
}

SliderAdapter.IconButtonWrapper = styled.div`
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 auto;
`;
